// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * VehicleData - Voertuigen & Mobiliteit
 * Veldenset 1:1 gereconstrueerd uit de V46-build (entry-default `kP`).
 * Opslag verloopt via wizardSave/wizardLoad met moduleKey 'vehicles'
 * (encrypted blob in db.wishes). Geen aparte Dexie-tabel, geen localStorage.
 */

export interface VehicleEntry {
  id:                   string
  // - Identificatie -
  kenteken:             string
  merk:                 string
  typeModel:            string
  bouwjaar:             string
  kleur:                string
  chassisnummer:        string
  categorie:            string
  // - Eigendom -
  eigendomssituatie:    string
  financieringsmaatschappij: string
  // - Locatie -
  locatieType:          string
  locatieStraat:        string
  locatieHuisnummer:    string
  locatiePostcode:      string
  locatiePlaats:        string
  locatieLand:          string
  // - Afhandeling -
  gewensteAfhandeling:  string
  instructies:          string
  // - Documenten & sleutels -
  kentekencard:         string
  tenaamstellingscode:  string
  reservesleutels:      string
  documentLocatie:      string
  documentLocatieNotes: string
  sleutelLocatie:       string
  sleutelLocatieNotes:  string
  // - Verzekering -
  verzekeraar:          string
  polisnummer:          string
  verzekeraarContact:   string
  // - Lease -
  heeftLease:           string
  leaseMaatschappij:    string
  leaseContractNr:      string
  leaseContact:         string
}

export interface VehicleData {
  vehicles: VehicleEntry[]
}

export const EMPTY_VEHICLE_ENTRY: Omit<VehicleEntry, 'id'> = {
  kenteken: '', merk: '', typeModel: '', bouwjaar: '', kleur: '',
  chassisnummer: '', categorie: '', eigendomssituatie: '', financieringsmaatschappij: '',
  locatieType: '', locatieStraat: '', locatieHuisnummer: '', locatiePostcode: '', locatiePlaats: '', locatieLand: 'Nederland',
  gewensteAfhandeling: '', instructies: '',
  kentekencard: '', tenaamstellingscode: '', reservesleutels: '',
  documentLocatie: '', documentLocatieNotes: '', sleutelLocatie: '', sleutelLocatieNotes: '',
  verzekeraar: '', polisnummer: '', verzekeraarContact: '',
  heeftLease: '', leaseMaatschappij: '', leaseContractNr: '', leaseContact: '',
}

export const EMPTY_VEHICLES: VehicleData = { vehicles: [] }

// ── Keuze-opties (Nederlandse opties o.b.v. V46-functionaliteit) ───────────────

export const VEHICLE_CATEGORIE_OPTIONS = [
  { value: 'personenauto',  label: 'Personenauto' },
  { value: 'bedrijfswagen', label: 'Bedrijfswagen' },
  { value: 'motor',         label: 'Motor' },
  { value: 'bromfiets',     label: 'Bromfiets' },
  { value: 'scooter',       label: 'Scooter' },
  { value: 'camper',        label: 'Camper' },
  { value: 'caravan',       label: 'Caravan' },
  { value: 'aanhangwagen',  label: 'Aanhangwagen' },
  { value: 'overig',        label: 'Overig' },
] as const

export const VEHICLE_EIGENDOM_OPTIONS = [
  { value: 'koop',         label: 'Koop / eigendom' },
  { value: 'financiering', label: 'Financiering / lening' },
  { value: 'lease',        label: 'Lease' },
  { value: 'zakelijk',     label: 'Zakelijk / op naam bedrijf' },
  { value: 'overig',       label: 'Overig' },
] as const

export const VEHICLE_LOCATIE_OPTIONS = [
  { value: 'garagebox',    label: 'Garagebox' },
  { value: 'oprit',        label: 'Oprit / eigen terrein' },
  { value: 'openbare_weg', label: 'Openbare weg' },
  { value: 'stalling',     label: 'Stalling / opslag' },
  { value: 'overig',       label: 'Overig' },
] as const

export const VEHICLE_AFHANDELING_OPTIONS = [
  { value: 'verkopen',   label: 'Verkopen' },
  { value: 'behouden',   label: 'Behouden in de familie' },
  { value: 'overdragen', label: 'Overdragen aan specifiek persoon' },
  { value: 'schenken',   label: 'Schenken' },
  { value: 'inruilen',   label: 'Inruilen' },
  { value: 'onbekend',   label: 'Nog onbekend' },
] as const

export const VEHICLE_LEASE_OPTIONS = [
  { value: 'yes',     label: 'Ja' },
  { value: 'no',      label: 'Nee' },
  { value: 'unknown', label: 'Onbekend' },
] as const

// V61: veelvoorkomende financieringsmaatschappijen (auto). 'Andere maatschappij'
// wordt door SelectWithOther toegevoegd; daarom hier niet opnemen.
export const VEHICLE_FINANCIER_OPTIONS: string[] = [
  'Santander Consumer Finance',
  'Volkswagen Pon Financial Services',
  'Stellantis Financial Services',
  'PSA Finance',
  'Renault Financial Services',
  'BMW Financial Services',
  'Mercedes-Benz Financial Services',
  'Toyota Financial Services',
  'Volvo Car Financial Services',
  'ALD Automotive / Ayvens',
  'Arval',
  'International Car Lease Holding',
  'ING Lease',
  'Rabobank Leasing',
  'ABN AMRO Lease',
]

// ── V61: autocomplete-/keuzelijsten (waarden blijven gewone tekst) ────────────
// Merken (auto + enkele tweewielers); vrije invoer blijft mogelijk via datalist.
export const VEHICLE_MERK_OPTIONS: string[] = [
  'Volkswagen', 'Toyota', 'Renault', 'Peugeot', 'Opel', 'Ford', 'BMW',
  'Mercedes-Benz', 'Audi', 'Citroën', 'Kia', 'Hyundai', 'Volvo', 'Škoda',
  'Nissan', 'Fiat', 'SEAT', 'Mazda', 'MINI', 'Suzuki', 'Dacia', 'Honda',
  'Tesla', 'Mitsubishi', 'Jeep', 'Land Rover', 'Jaguar', 'Porsche', 'Smart',
  'Alfa Romeo', 'DS', 'Cupra', 'MG', 'Lexus', 'Subaru', 'Chevrolet',
  'Yamaha', 'Kawasaki', 'Ducati', 'Harley-Davidson', 'Piaggio', 'Vespa',
]

// Modellen per merk (top-modellen NL). Merken zonder lijst → vrije invoer.
export const VEHICLE_MODEL_OPTIONS: Record<string, string[]> = {
  'Volkswagen':    ['Up!', 'Polo', 'Golf', 'ID.3', 'ID.4', 'T-Roc', 'T-Cross', 'Tiguan', 'Touran', 'Passat', 'Arteon', 'Caddy', 'Transporter'],
  'Toyota':        ['Aygo', 'Aygo X', 'Yaris', 'Yaris Cross', 'Corolla', 'C-HR', 'RAV4', 'Auris', 'Prius', 'ProAce', 'Hilux'],
  'Renault':       ['Twingo', 'Clio', 'Captur', 'Mégane', 'Scénic', 'Kadjar', 'Arkana', 'Austral', 'Zoe', 'Kangoo', 'Trafic'],
  'Peugeot':       ['108', '208', 'e-208', '2008', 'e-2008', '308', '3008', '5008', '508', 'Partner', 'Expert'],
  'Opel':          ['Karl', 'Corsa', 'Astra', 'Mokka', 'Crossland', 'Grandland', 'Insignia', 'Zafira', 'Combo', 'Vivaro'],
  'Ford':          ['Ka', 'Fiesta', 'Focus', 'Puma', 'Kuga', 'Mondeo', 'EcoSport', 'C-Max', 'S-Max', 'Transit', 'Mustang', 'Mustang Mach-E'],
  'BMW':           ['1-serie', '2-serie', '3-serie', '4-serie', '5-serie', 'X1', 'X2', 'X3', 'X5', 'i3', 'i4', 'iX'],
  'Mercedes-Benz': ['A-Klasse', 'B-Klasse', 'C-Klasse', 'E-Klasse', 'CLA', 'GLA', 'GLC', 'GLE', 'Vito', 'Sprinter', 'EQA', 'EQC'],
  'Audi':          ['A1', 'A3', 'A4', 'A5', 'A6', 'Q2', 'Q3', 'Q5', 'Q7', 'e-tron', 'TT'],
  'Citroën':       ['C1', 'C3', 'C3 Aircross', 'C4', 'ë-C4', 'C5 Aircross', 'Berlingo', 'SpaceTourer'],
  'Kia':           ['Picanto', 'Rio', 'Ceed', 'Stonic', 'Niro', 'e-Niro', 'Sportage', 'Sorento', 'EV6', 'XCeed'],
  'Hyundai':       ['i10', 'i20', 'i30', 'Kona', 'Bayon', 'Tucson', 'Santa Fe', 'Ioniq', 'Ioniq 5'],
  'Volvo':         ['V40', 'V60', 'V90', 'S60', 'S90', 'XC40', 'XC60', 'XC90', 'C40'],
  'Škoda':         ['Citigo', 'Fabia', 'Scala', 'Octavia', 'Superb', 'Kamiq', 'Karoq', 'Kodiaq', 'Enyaq'],
  'Nissan':        ['Micra', 'Note', 'Juke', 'Qashqai', 'X-Trail', 'Leaf', 'Ariya'],
  'Fiat':          ['500', '500e', 'Panda', 'Tipo', '500X', '500L', 'Punto', 'Doblò'],
  'Tesla':         ['Model 3', 'Model Y', 'Model S', 'Model X'],
}

// Veelvoorkomende kleuren; vrije invoer blijft mogelijk via datalist.
export const VEHICLE_KLEUR_OPTIONS: string[] = [
  'Zwart', 'Wit', 'Grijs', 'Zilver', 'Antraciet', 'Blauw', 'Donkerblauw',
  'Lichtblauw', 'Rood', 'Bordeauxrood', 'Groen', 'Geel', 'Oranje', 'Bruin',
  'Beige', 'Goud', 'Paars', 'Metallic grijs', 'Metallic blauw', 'Metallic zwart',
]

// Veelvoorkomende autoverzekeraars. 'Andere verzekeraar' voegt SelectWithOther toe.
export const VEHICLE_VERZEKERAAR_OPTIONS: string[] = [
  'Centraal Beheer', 'Univé', 'Interpolis', 'ANWB', 'FBTO', 'Aegon',
  'Nationale-Nederlanden', 'a.s.r.', 'Allianz', 'Reaal', 'OHRA', 'Ditzo',
  'InShared', 'Allsecur', 'Klaverblad', 'Unigarant', 'ZLM', 'Kroodle',
  'Promovendum', 'HEMA Verzekeringen',
]

// Veelvoorkomende leasemaatschappijen. 'Andere maatschappij' via SelectWithOther.
export const VEHICLE_LEASE_MAATSCHAPPIJ_OPTIONS: string[] = [
  'LeasePlan', 'Athlon', 'Ayvens (ALD Automotive)', 'Arval', 'Alphabet',
  'Volkswagen Leasing', 'MultiLease', 'Justlease', 'Terberg Leasing',
  'Hiltermann Lease', 'Business Lease', 'Wagenplan',
]
